import { Autocomplete, Button, Pagination, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ModeloCadastroItem from "./ModeloCadastroItem";
import { ToastContainer } from "react-toastify";
import { useEffect, useState } from "react";
import { CadastroModelo } from "../../../interface/ICadastroModelo";
import { empresaType } from "../../../context/EmpresasContext";

export type ListaCadastroModeloTextos = {
  botaoNovo: string;
  busca: string;
  descricaoVazia: string;
  erro: string;
  instrucaoSemEmpresa: string;
};

const textosPadrao: ListaCadastroModeloTextos = {
  botaoNovo: 'Criar Cadastro-Modelo',
  busca: 'Buscar cadastros-modelo',
  descricaoVazia: 'Esta empresa ainda não possui cadastros-modelo.',
  erro: 'Erro ao buscar cadastros-modelo',
  instrucaoSemEmpresa: 'Selecione uma empresa para visualizar os cadastros-modelo',
};

export function ListaModelos({ cadastroModelos, textos = textosPadrao, onItemClick, onDuplicate, showDuplicate = true, inactivateModelo, deleteLabel }: { cadastroModelos: any; textos?: ListaCadastroModeloTextos; onItemClick?: (form: CadastroModelo) => void; onDuplicate?: (form: CadastroModelo) => void; showDuplicate?: boolean; inactivateModelo?: any; deleteLabel?: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagina, setPagina] = useState(1);
  const modelosFiltrados = (Array.isArray(cadastroModelos.data) ? cadastroModelos.data : [])
    .slice()
    .sort((primeiro: CadastroModelo, segundo: CadastroModelo) => Number(segundo.dataCriacao || 0) - Number(primeiro.dataCriacao || 0))
    .filter((form: CadastroModelo | null) => (form?.nome || '').toLowerCase().includes(searchParams.get('search')?.toLowerCase() || ''))
    .filter((form: CadastroModelo | null): form is CadastroModelo => !!form);
  const totalPaginas = Math.max(1, Math.ceil(modelosFiltrados.length / 5));

  useEffect(() => {
    setPagina(1);
  }, [searchParams.get('search')]);

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  if (cadastroModelos.data && cadastroModelos.data.length === 0 && !(cadastroModelos.isLoading || cadastroModelos.isFetching) && !cadastroModelos.isError) {
    return (
      <Typography variant="h6" textAlign="center" width="100%" color="textSecondary">
        {textos.descricaoVazia}<br />Clique em "{textos.botaoNovo}" para adicionar o primeiro modelo.
      </Typography>
    )
  }

  return (
    <Stack direction='column' gap={0.5} width='100%'>
      {cadastroModelos.isLoading || cadastroModelos.isFetching ? Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={60} width={'100%'} sx={{ borderRadius: 2, animationDelay: `${i * 0.1}s` }} />)
      ) : cadastroModelos.isError ? (
        <Typography variant="h6" textAlign="center" width="100%" color="error">
          {textos.erro}
        </Typography>
      ) : (
        <>
          <TextField label={textos.busca} value={searchParams.get('search') || ''} onChange={(e) => setSearchParams({ search: e.target.value })} fullWidth variant="outlined" sx={{ bgcolor: 'grey.50', marginBottom: 2 }} />
          {searchParams.get('search') &&
            <Typography variant="body2" color="textSecondary">
              Resultados para "{searchParams.get('search')}"
            </Typography>}
          {modelosFiltrados
            .slice((pagina - 1) * 5, pagina * 5)
            .map((form: CadastroModelo) => (
              <ModeloCadastroItem key={form.id} form={form} duplicateCadastroModelo={cadastroModelos.duplicateCadastroModelo} inactivateCadastroModelo={inactivateModelo || cadastroModelos.inactivateCadastroModelo} onItemClick={onItemClick} onDuplicate={onDuplicate} showDuplicate={showDuplicate} deleteLabel={deleteLabel} />
            ))}
          {totalPaginas > 1 && <Pagination count={totalPaginas} page={pagina} onChange={(_, novaPagina) => setPagina(novaPagina)} color='primary' sx={{ alignSelf: 'center', mt: 2 }} />}
        </>
      )}
    </Stack>
  )
}

export default function ListaCadastroModelos({ companyId, cadastroModelos, textos = textosPadrao, onCreate, onItemClick, onDuplicate, showDuplicate = true, inactivateModelo, deleteLabel }: { companyId: string | undefined, cadastroModelos: any; textos?: ListaCadastroModeloTextos; onCreate?: () => void; onItemClick?: (form: CadastroModelo) => void; onDuplicate?: (form: CadastroModelo) => void; showDuplicate?: boolean; inactivateModelo?: any; deleteLabel?: string }) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [empresaSelecionada, setEmpresaSelecionada] = useState<empresaType | null>(null);

  useEffect(() => {
    setEmpresaSelecionada(cadastroModelos.empresas?.find((empresa: empresaType) => empresa.id === companyId) || null);
  }, [cadastroModelos.empresas, companyId]);

  return (
    <Stack direction='column' alignItems='flex-start' justifyContent='flex-start' width='100%' paddingX={'10%'} spacing={3}>
      <Stack direction="row" width='100%' gap={2}>
        <Autocomplete
          fullWidth
          getOptionLabel={option => option?.nome || ''}
          getOptionKey={option => option.id as string}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={cadastroModelos.empresasLoading || cadastroModelos.empresasFetching}
          loadingText='Buscando empresas...'
          disabled={cadastroModelos.empresasLoading || cadastroModelos.empresasFetching}
          noOptionsText={'Não encontrou empresas'}
          clearOnEscape
          value={empresaSelecionada}
          options={cadastroModelos.empresas
            ?.sort((a: empresaType, b: empresaType) => { return a?.nome?.toLowerCase() > b?.nome?.toLowerCase() ? 1 : -1 })
            ?.filter((elem: empresaType) => elem?.status === 'ativo')
            ?.map((empresa: empresaType) => ({
              ...empresa
            })) || []}
          onChange={(_event: any, newValue: empresaType | null) => {
            const newId = newValue?.id || '';
            if (newId) {
              setEmpresaSelecionada(newValue);
              navigate(`/cadastro-modelo/${newId}`);
            } else {
              // Limpar a empresa também remove os modelos e filtros exibidos abaixo.
              setEmpresaSelecionada(null);
              navigate('/cadastro-modelo');
              setSearchParams({});
            }
          }}
          renderInput={(params) => <TextField {...params} label={'Selecione a empresa'} />}
          renderOption={(props, option: any) => (
            <li {...props} key={option.id}>
              <span>{option?.nome || ''}</span>
            </li>)}
        />
        <Button variant="contained" disabled={!companyId} color="primary" size="large" sx={{ textWrap: 'nowrap', px: 2, flexShrink: 0 }} startIcon={<AddCircleOutlineOutlinedIcon />} onClick={() => onCreate ? onCreate() : navigate(`/cadastro-modelo/${companyId}/novo`)}>{textos.botaoNovo}</Button>
      </Stack>
      {companyId ? <ListaModelos cadastroModelos={cadastroModelos} textos={textos} onItemClick={onItemClick} onDuplicate={onDuplicate} showDuplicate={showDuplicate} inactivateModelo={inactivateModelo} deleteLabel={deleteLabel} /> : <Typography variant="h6" textAlign="center" width="100%" color="textSecondary">{textos.instrucaoSemEmpresa}</Typography>}
      <ToastContainer />
    </Stack>
  )
}
